import React from 'react';
import { ModalController } from '../../../../components/Modal/ControlledModal';
import { useChanges, Form } from '../../../../components/Form/Form';
import { Address } from '../../../../model/company';
import { Label } from '../../../../components/Form/Label/Label';
import { TextField } from '../../../../components/Form/TextField/TextField';
import { useRemoteData } from '../../../../model/data';
import { getCountries } from '../../../../model/country';
import { Select } from '../../../../components/Form/Select/Select';
import { sortBy } from 'lodash';
import { handleError } from '../../../../model/notification';
import { Invoice, updateInvoice } from '../../../../model/invoice';
import { Order } from '../../../../model/order';
import { t } from '../../../../locales/translate';
import { uniqBy } from 'lodash';
import { Stack } from '../../../../components/Stack/Stack';

interface Props {
  invoice: Invoice & { order: Order };
  controller: ModalController<Address>;
  onSubmit: () => void;
}

export function EditInvoice({ invoice, controller, onSubmit }: Props): JSX.Element {
  const [changes, , model, trackChanges] = useChanges<Address>(controller.model);
  const [countries] = useRemoteData(getCountries, (db) => {
    return uniqBy(db.table('countries').all, 'countryCode');
  });

  async function handleOnSubmit(): Promise<void> {
    try {
      await updateInvoice({
        orderId: invoice.order.id,
        invoiceId: invoice.id,
        address: {
          ...model,
          ...changes,
        },
      });
      onSubmit();
      controller.close();
    } catch (error) {
      handleError(error);
    }
  }

  return (
    <Form model={model} onInput={trackChanges}>
      {(form) => {
        controller.configure({
          onSubmit: handleOnSubmit,
          canSubmit: form.valid,
        });

        return (
          <Stack>
            <Label title={t.model.invoice.customerData} />
            <TextField name="firstName" label={t.model.user.firstName} />
            <TextField name="lastName" label={t.model.user.lastName} />
            <TextField name="company" label={t.model.company._name} />
            <TextField name="address" label={t.model.address.street} />
            <TextField name="address2" label={t.model.address.additionalStreet} />
            <TextField name="postCode" label={t.model.address.postCode} />
            <TextField name="city" label={t.model.address.city} />
            <Select
              name="countryCode"
              label={t.model.address.country}
              defaultValue={model.countryCode}
              options={sortBy(countries, 'name').map((c) => ({
                label: c.name,
                value: c.countryCode,
              }))}
              allowEmpty
            />
          </Stack>
        );
      }}
    </Form>
  );
}
